import React from 'react';
import {
  CartesianGrid, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import Text from '@/components/ui/Text';
import type { TimeRange } from './deriveParticipantStates';
import { buildSlideSyncSeries, type SlideSyncPoint } from './slideSyncSeries';
import type { LiveSessionEvent, ParticipantStudent } from './types';

interface SlideSyncTrackProps {
  end: number;
  events: LiveSessionEvent[];
  selectedStudent: ParticipantStudent | null;
  start: number;
  teacherDrops: TimeRange[];
}

interface StudentDotProps {
  cx?: number;
  cy?: number;
  payload?: SlideSyncPoint;
}

const TEACHER_COLOR = '#522e91';
const STUDENT_COLOR = '#2b9ab8';
const OWN_NAV_COLOR = '#b45309';
const SECTION_CLASS = [
  'tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-p-4',
  'tw-flex tw-flex-col tw-gap-2',
].join(' ');
const LEGEND_ITEM_CLASS = 'tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-very-dark-grey';

const formatTick = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = Math.abs(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Only the student's own nav points get a marker: a filled dot when they landed on the teacher's
// slide, a hollow amber ring when they navigated by themselves.
const StudentDot = ({ cx, cy, payload }: StudentDotProps) => {
  if (cx === undefined || cy === undefined || !payload?.studentCause) return null;
  if (payload.studentCause === 'user') {
    return <circle cx={cx} cy={cy} fill="#fff" r={4} stroke={OWN_NAV_COLOR} strokeWidth={2} />;
  }
  return <circle cx={cx} cy={cy} fill={STUDENT_COLOR} r={2.5} />;
};

const formatTooltipValue = (value: number, name: string, item: { payload?: SlideSyncPoint }) => {
  const own = name !== 'Teacher' && item.payload?.studentCause === 'user' ? ' · browsed on their own' : '';
  return [`slide ${value}${own}`, name];
};

// Teacher's slide over time (step) against the selected student's shown slide, both carried to
// the end of the window. Lines apart = the student is not where the teacher is; a hollow ring
// marks a slide the student chose themselves.
const SlideSyncTrack = ({ end, events, selectedStudent, start, teacherDrops }: SlideSyncTrackProps) => {
  const { maxSlide, points, usesPositions } = buildSlideSyncSeries(events, {
    end, start, studentId: selectedStudent?.id ?? null,
  });
  const domainEnd = Math.max(60, Math.round((end - start) / 1000));

  if (points.length === 0) return null;

  return (
    <section aria-label="Slide sync" className={SECTION_CLASS}>
      <div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
        <Text className="tw-text-tci-black tw-m-0" size="sm" tag="h2" variant="label">Slide sync</Text>
        <span className={LEGEND_ITEM_CLASS}>
          <span aria-hidden className="tw-h-0.5 tw-w-4" style={{ backgroundColor: TEACHER_COLOR }} /> Teacher
        </span>
        <span className={LEGEND_ITEM_CLASS}>
          <span aria-hidden className="tw-h-0.5 tw-w-4" style={{ backgroundColor: STUDENT_COLOR }} />
          {selectedStudent ? selectedStudent.full_name : 'Select a student to compare'}
        </span>
        {selectedStudent && (
          <span className={LEGEND_ITEM_CLASS}>
            <span
              aria-hidden
              className="tw-h-2 tw-w-2 tw-rounded-full tw-border-2 tw-border-solid tw-bg-white"
              style={{ borderColor: OWN_NAV_COLOR }}
            />
            browsed on their own
          </span>
        )}
        {!usesPositions && (
          <span className="tw-text-xs tw-text-very-dark-grey tw-ml-auto">
            Slides numbered by id order (no positions reported)
          </span>
        )}
      </div>
      <div className="tw-h-52">
        <ResponsiveContainer height="100%" width="100%">
          <LineChart data={points} margin={{ bottom: 8, left: 0, right: 16, top: 8 }}>
            <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
            <XAxis
              dataKey="t"
              domain={[0, domainEnd]}
              tick={{ fontSize: 11 }}
              tickFormatter={formatTick}
              type="number"
            />
            <YAxis
              allowDecimals={false}
              domain={[1, Math.max(2, maxSlide + 1)]}
              label={{ angle: -90, fontSize: 11, position: 'insideLeft', value: 'Slide' }}
              tick={{ fontSize: 11 }}
              width={40}
            />
            <Tooltip
              formatter={formatTooltipValue}
              labelFormatter={(label: number) => `t+${formatTick(label)}`}
            />
            {teacherDrops.map(drop => (
              <ReferenceArea
                key={drop.start}
                fill="#dc2626"
                fillOpacity={0.12}
                x1={Math.round((drop.start - start) / 1000)}
                x2={Math.round((drop.end - start) / 1000)}
              />
            ))}
            <Line
              dataKey="teacher"
              dot={false}
              isAnimationActive={false}
              name="Teacher"
              stroke={TEACHER_COLOR}
              strokeWidth={2}
              type="stepAfter"
            />
            {selectedStudent && (
              <Line
                activeDot={{ r: 4 }}
                dataKey="student"
                dot={<StudentDot />}
                isAnimationActive={false}
                name={selectedStudent.full_name}
                stroke={STUDENT_COLOR}
                strokeWidth={2}
                type="stepAfter"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

export default SlideSyncTrack;
